import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/auth` : 'https://backend-c-0mxm.onrender.com/api/auth';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor để tự động thêm Authorization header
api.interceptors.request.use(
  (config) => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.userId || user.token) {
          config.headers.Authorization = `Bearer ${user.userId || user.token}`;
        }
      }
    } catch (error) {
      console.warn('Error parsing user data from localStorage:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor để handle token expiry
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const handleError = (error) => {
  if (error.response) {
    return {
      message: error.response.data.message || 'Có lỗi xảy ra từ server',
      status: error.response.status,
    };
  } else if (error.request) {
    return {
      message: 'Không thể kết nối đến server',
      status: 0,
    };
  } else {
    return {
      message: 'Có lỗi xảy ra khi tạo request',
      status: 0,
    };
  }
};

export const login = async (username, password) => {
  try {
    console.log('Attempting login with:', { username, password });
    const response = await api.post('/login', { username, password });
    console.log('Login response:', response.data);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Login error:', error.response?.data || error);
    return {
      success: false,
      error: handleError(error),
    };
  }
};

export const getAllUsers = async () => {
  try {
    const response = await api.get('/users');
    // Backend trả về { success: true, data: [...] }
    // Chúng ta cần extract data array
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data, // Extract actual users array
      };
    } else {
      return {
        success: false,
        error: { message: response.data.message || 'Unknown error' },
      };
    }
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

export const createUser = async (userData) => {
  try {
    const response = await api.post('/users', userData);
    // Backend trả về { success: true, data: user, message: "..." }
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } else {
      return {
        success: false,
        error: { message: response.data.message || 'Unknown error' },
      };
    }
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

export const updateUser = async (id, userData) => {
  try {
    const response = await api.put(`/users/${id}`, userData);
    // Backend trả về { success: true, data: user, message: "..." }
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } else {
      return {
        success: false,
        error: { message: response.data.message || 'Unknown error' },
      };
    }
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

export const deleteUser = async (id) => {
  try {
    const response = await api.delete(`/users/${id}`);
    // Backend trả về { success: true, message: "..." }
    if (response.data.success) {
      return {
        success: true,
        message: response.data.message,
      };
    } else {
      return {
        success: false,
        error: { message: response.data.message || 'Unknown error' },
      };
    }
  } catch (error) {
    return {
      success: false,
      error: handleError(error),
    };
  }
};

// Get current user info (to refresh user data)
export const getCurrentUserInfo = async () => {
  try {
    const response = await api.get('/me');
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      return {
        success: false,
        error: { message: response.data.message || 'Unknown error' }
      };
    }
  } catch (error) {
    return {
      success: false,
      error: handleError(error)
    };
  }
};

// Refresh current user's data in localStorage
export const refreshUserData = async () => {
  try {
    const userData = localStorage.getItem('user');
    if (!userData) {
      return { success: false, error: { message: 'No user logged in' } };
    }
    
    const user = JSON.parse(userData);
    const result = await getCurrentUserInfo();
    
    if (result.success) {
      // Update localStorage with new data, keep existing login info
      const updatedUser = {
        userId: user.userId,
        username: user.username || result.data.username,
        fullName: result.data.fullName,
        role: result.data.role,
        token: user.token,
        allowedTimeSlots: result.data.allowedTimeSlots || [],
        allowedPlatforms: result.data.allowedPlatforms || [],
        platformConfigs: result.data.platformConfigs || {},
        departmentRoles: result.data.departmentRoles || [],
        currentDepartment: user.currentDepartment,
        departmentConfigs: result.data.departmentConfigs || {}
      };
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return {
        success: true,
        data: updatedUser,
        message: 'Đã cập nhật thông tin mới'
      };
    } else {
      return result;
    }
  } catch (error) {
    return {
      success: false,
      error: handleError(error)
    };
  }
}; 